import React, {useState, useEffect} from 'react';
import Tree from 'react-d3-tree';

import "./ProblemTree.css"
import {deleteProblem, viewProblem, viewProblemOverview} from "../../../../services/ProblemService";
import {StandardButton} from "../../../common/button/standard-button/StandardButton";
import {viewChangeIdea} from "../../../../services/ChangeIdeaService";
import {viewCause} from "../../../../services/CauseService";
import {retrieveVisitWithPeople} from "../../../../services/VisitService";
import {retrieveIndicator} from "../../../../services/IndicatorService";
import {retrieveAllGrantsForSystemUser, retrieveGrant} from "../../../../services/GrantService";
import {retrieveStaffMember} from "../../../../services/StaffMemberService";
import {createPersonDropdownDisplayValue} from "../../../../util/PersonUtil";
import {viewLocation} from "../../../../services/FacilityService";
import {ConfirmDeleteModal} from "../../../common/confirmation-modal/ConfimDeleteModal";

export const ProblemTree = ({context, setContext, navigateTo, setSpinnerText, showToaster}) => {

    const [problemTreeData, setProblemTreeData] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState(null);
    const [extRefToBeDeleted, setExtRefToBeDeleted] = useState(null);

    let isAdmin;
    if (null !== context.roles) {
        isAdmin = context.roles.includes("qi-admin");
    }

    useEffect(() => {
        const fetchData = async () => {
            setSpinnerText("Retrieving Problem Overview");
            await determineEditMode();
            const problemExternalReference = context.problemToBeViewedExternalReference;
            const [data, error] = await viewProblemOverview(context.username, problemExternalReference);
            if (data) {
                if (data.responseStatus === "Success") {
                    setProblemTreeData(data.problemOverview);
                } else {
                    showToaster(data);
                }
                setSpinnerText(null);
            } else {
                showToaster({
                    responseStatus: "Error",
                    responseMessage: error.message,
                });
                setSpinnerText(null);
            }
        }
        fetchData();
    }, []);

    const determineEditMode = async () => {
        const problemExternalReference = context.problemToBeViewedExternalReference;
        const retrievedProblem = await fetchProblem(problemExternalReference);
        const retrievedProblemGrant = await fetchGrant(retrievedProblem.grantExternalRef);
        const userGrantList = await fetchGrantsForUser(context.username, context.loggedInUser.externalReference);
        let mode = false;
        if (retrievedProblemGrant && userGrantList) {
            userGrantList.forEach((grant) => {
                if (grant.externalReference === retrievedProblemGrant.externalReference) {
                    mode = true;
                }
            });
        }
        setContext((prevState) => ({
            ...prevState,
            editMode: mode,
        }));
    }

    const buildProblemTree = () => {
        let mappedRootCauseList = [];
        if (problemTreeData.routeCauseList) {
            mappedRootCauseList = problemTreeData.routeCauseList.map((rootCause) => {
                let mappedChangeIdeaList = [];
                if (rootCause.changeIdeaList) {
                    mappedChangeIdeaList = rootCause.changeIdeaList.map((changeIdea) => {
                        let mappedPDSAList = [];
                        if (changeIdea.pdsalist) {
                            mappedPDSAList = changeIdea.pdsalist.map((PDSA) => {
                                return {
                                    name: PDSA.name + " - " + PDSA.status,
                                    attributes: {
                                        type: "PDSA",
                                        PDSAExternalReference: PDSA.externalReference,
                                        visitExternalReference: problemTreeData.visitExternalReference,
                                        changeIdeaExternalReference: changeIdea.externalReference,
                                        color: PDSA.colour
                                    },
                                    children: [],
                                }
                            });
                        }

                        return {
                            name: changeIdea.name,
                            attributes: {
                                type: "Change Idea",
                                changeIdeaExternalReference: changeIdea.externalReference,
                            },
                            children: mappedPDSAList,
                        }
                    });
                }

                return {
                    name: rootCause.name,
                    attributes: {
                        type: "Root Cause",
                        rootCauseExternalReference: rootCause.externalReference,
                    },
                    children: mappedChangeIdeaList,
                }
            });
        }

        return {
            name: problemTreeData.locationName,
            attributes: {
                type: "Location",
                locationExternalReference: problemTreeData.locationExternalReference,
            },
            children: [
                {
                    name: problemTreeData.topicAreaName,
                    attributes: {
                        type: "Visit",
                        problemExternalReference: problemTreeData.problemExternalReference,
                    },
                    children: [
                        {
                            name: problemTreeData.problemName,
                            attributes: {
                                type: "Problem",
                                problemExternalReference: problemTreeData.problemExternalReference,
                            },
                            children: mappedRootCauseList,
                        }
                    ],
                }
            ],
        };
    }

    const fetchIndicator = async (indicatorExternalReference) => {
        let savedIndicator = null;
        const [data, error] = await retrieveIndicator(context.username, indicatorExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedIndicator = data.indicatorVOToView;
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedIndicator;
    }

    const fetchVisit = async (visitExternalReference) => {
        let savedVisit = null;
        const [data, error] = await retrieveVisitWithPeople(context.username, visitExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedVisit = data.visitVOToView;
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedVisit;
    }

    const fetchProblem = async (problemExternalReference) => {
        let savedProblem = null;
        const [data, error] = await viewProblem(context.username, problemExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedProblem = data.problemVOToView;
            } else {
                showToaster(error);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedProblem;
    }

    const fetchCause = async (causeExternalReference) => {
        let savedCause = null;
        const [data, error] = await viewCause(context.username, causeExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedCause = data.causeVOToView;
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedCause;
    }

    const fetchChangeIdea = async (changeIdeaExternalReference) => {
        let savedChangeIdea = null;
        const [data, error] = await viewChangeIdea(context.username, changeIdeaExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedChangeIdea = data.changeIdeaVOToView;
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedChangeIdea;
    }

    const fetchGrant = async (grantExternalReference) => {
        let savedGrant = null;
        const [data, error] = await retrieveGrant(context.username, grantExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedGrant = data.grantVOToView;
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedGrant;
    }

    const fetchGrantsForUser = async (username, externalReference) => {
        let userGrants = null;
        const [data, error] = await retrieveAllGrantsForSystemUser(username, externalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                userGrants = data.grantVOS.filter((grant) => grant.active);
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return userGrants;
    }

    const fetchStaffMember = async (systemUserExternalReference) => {
        let savedStaffMember = null;
        const [data, error] = await retrieveStaffMember(context.username, systemUserExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedStaffMember = data.systemUserVOToView;
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedStaffMember;
    }

    const fetchLocation = async (locationExternalReference) => {
        let savedLocation = null;
        const [data, error] = await viewLocation(context.username, {externalReference: locationExternalReference});
        if (data) {
            if (data.responseStatus === "Success") {
                savedLocation = data.foundLocationVO;
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedLocation;
    }

    const handleNodeClick = async (nodeData) => {
        const {attributes} = nodeData;
        if (attributes.type === "PDSA") {
            await handlePDSAClick(attributes);
        } else if (attributes.type === "Change Idea") {
            await handleChangeIdeaClick(attributes.changeIdeaExternalReference);
        } else if (attributes.type === "Root Cause") {
            await handleRootCauseClick(attributes.rootCauseExternalReference);
        } else if (attributes.type === "Problem") {
            await handleProblemClick(attributes.problemExternalReference);
        } else if (attributes.type === "Visit") {
            await handleVisitClick(attributes.problemExternalReference);
        } else if (attributes.type === "Location") {
            await handleLocationClick(attributes.locationExternalReference);
        }
    }

    const handlePDSAClick = async (attributes) => {
        setSpinnerText("Retrieving PDSA");
        const {changeIdeaExternalReference} = attributes;
        let savedChangeIdea = await fetchChangeIdea(changeIdeaExternalReference);

        if (savedChangeIdea) {

            let toImprove = null;
            let from = null;
            let to = null;
            let byDate = null;

            if (savedChangeIdea.aimStatementVO) {
                toImprove = savedChangeIdea.aimStatementVO.toImprove;
                from = savedChangeIdea.aimStatementVO.from;
                to = savedChangeIdea.aimStatementVO.to;
                byDate = savedChangeIdea.aimStatementVO.by;
            }

            setContext((prevState) => ({
                ...prevState,
                viewMode: true,
                breadcrumbs: [],
                changeIdeaScreen: {
                    changeConcept: savedChangeIdea.concept,
                    priority: {label: savedChangeIdea.priority, value: savedChangeIdea.priority,},
                    description: savedChangeIdea.description,
                    prediction: savedChangeIdea.prediction,
                    toImprove,
                    from,
                    to,
                    byDate,
                    processMeasureList: savedChangeIdea.processMeasureVOList,
                    PDSAList: [],
                    externalReference: savedChangeIdea.externalReference,
                    causeExternalReference: savedChangeIdea.causeExternalReference,
                    visitExternalReference: savedChangeIdea.visitExternalReference,
                    topicAreaExternalReference: savedChangeIdea.topicAreaExternalReference,
                    title: savedChangeIdea.title,
                },
                copyOfTopicAreaScreen: null,
                copyOfProblemScreen: null,
                copyOfRootCauseScreen: null,
                copyOfChangeIdeaScreen: null,
                problemScreen: {
                    externalReference: problemTreeData.problemExternalReference,
                    title: problemTreeData.problemName,
                },
                rootCauseScreen: {
                    externalReference: savedChangeIdea.causeExternalReference,
                    name: savedChangeIdea.causeName,
                },
                topicAreaScreen: {
                    visitExternalReference: problemTreeData.visitExternalReference,
                    topicArea: {externalReference: problemTreeData.topicAreaExternalReference}
                },
                facilityScreen: null,
            }));
            navigateTo("PDSAOverview");
        }
        setSpinnerText(null);
    }

    const handleChangeIdeaClick = async (externalReference) => {
        setSpinnerText("Retrieving Change Idea");
        let savedChangeIdea = await fetchChangeIdea(externalReference);

        if (savedChangeIdea) {

            let toImprove = null;
            let from = null;
            let to = null;
            let byDate = null;

            if (savedChangeIdea.aimStatementVO) {
                toImprove = savedChangeIdea.aimStatementVO.toImprove;
                from = savedChangeIdea.aimStatementVO.from;
                to = savedChangeIdea.aimStatementVO.to;
                byDate = savedChangeIdea.aimStatementVO.by;
            }

            setContext((prevState) => ({
                ...prevState,
                viewMode: true,
                breadcrumbs: [],
                changeIdeaScreen: {
                    changeConcept: savedChangeIdea.concept,
                    priority: {label: savedChangeIdea.priority, value: savedChangeIdea.priority,},
                    description: savedChangeIdea.description,
                    prediction: savedChangeIdea.prediction,
                    toImprove,
                    from,
                    to,
                    byDate,
                    processMeasureList: savedChangeIdea.processMeasureVOList,
                    PDSAList: [],
                    externalReference: savedChangeIdea.externalReference,
                    causeExternalReference: savedChangeIdea.causeExternalReference,
                    visitExternalReference: savedChangeIdea.visitExternalReference,
                    topicAreaExternalReference: savedChangeIdea.topicAreaExternalReference,
                    title: savedChangeIdea.title,
                    changeIdeaFileList: savedChangeIdea.changeIdeaFileList,
                },
                copyOfTopicAreaScreen: null,
                copyOfProblemScreen: null,
                copyOfRootCauseScreen: null,
                copyOfChangeIdeaScreen: null,
                problemScreen: {
                    externalReference: problemTreeData.problemExternalReference,
                    title: problemTreeData.problemName,
                },
                rootCauseScreen: {
                    externalReference: savedChangeIdea.causeExternalReference,
                    name: savedChangeIdea.causeName,
                },
                topicAreaScreen: {
                    visitExternalReference: problemTreeData.visitExternalReference,
                    topicArea: {externalReference: problemTreeData.topicAreaExternalReference}
                },
                facilityScreen: null,
            }));
            navigateTo("changeIdeaOne");
        }
        setSpinnerText(null);
    }

    const handleRootCauseClick = async (externalReference) => {
        setSpinnerText("Retrieving Root Cause");
        let savedCause = await fetchCause(externalReference);

        if (savedCause) {
            setContext((prevState) => ({
                ...prevState,
                viewMode: true,
                breadcrumbs: [],
                changeIdeaScreen: null,
                copyOfTopicAreaScreen: null,
                copyOfProblemScreen: null,
                copyOfRootCauseScreen: null,
                copyOfChangeIdeaScreen: null,
                problemScreen: {
                    externalReference: problemTreeData.problemExternalReference,
                    title: problemTreeData.problemName,
                },
                rootCauseScreen: {
                    externalReference: savedCause.externalReference,
                    name: savedCause.name,
                    description: savedCause.description,
                    fileList: savedCause.causeEvidenceList,
                    priority: {label: savedCause.priority, value: savedCause.priority},
                },
                topicAreaScreen: {
                    visitExternalReference: problemTreeData.visitExternalReference,
                    topicArea: {externalReference: problemTreeData.topicAreaExternalReference}
                },
                facilityScreen: null,
            }));
            navigateTo("rootCauseOne");
        }
        setSpinnerText(null);
    }

    const handleProblemClick = async (externalReference) => {
        setSpinnerText("Retrieving Problem");
        let savedVisit = null;
        let savedIndicator = null;
        const savedProblem = await fetchProblem(externalReference);

        if (savedProblem) {
            savedVisit = await fetchVisit(savedProblem.visitExternalRef);
        }

        if (savedVisit) {
            savedIndicator = await fetchIndicator(savedVisit.indicatorExternalReference);
        }

        if (savedIndicator) {
            setContext((prevState) => ({
                ...prevState,
                viewMode: true,
                breadcrumbs: [],
                changeIdeaScreen: null,
                copyOfTopicAreaScreen: null,
                copyOfProblemScreen: null,
                copyOfRootCauseScreen: null,
                copyOfChangeIdeaScreen: null,
                problemScreen: {
                    externalReference: savedProblem.externalReference,
                    title: savedProblem.name,
                    description: savedProblem.description,
                    nextVisitDate: savedVisit.dateOfNextVisit,
                    frequency: {
                        label: savedProblem.reviewFrequency,
                        value: savedProblem.reviewFrequency
                    },
                    baselines: savedProblem.outcomeMeasureBaselines,
                    toImprove: savedProblem.aimStatementVO.toImprove,
                    from: savedProblem.aimStatementVO.from,
                    to: savedProblem.aimStatementVO.to,
                    byDate: savedProblem.aimStatementVO.by,
                },
                rootCauseScreen: null,
                topicAreaScreen: {
                    visitExternalReference: savedProblem.visitExternalRef,
                    indicator: {
                        outcomeMeasure: savedIndicator.outcomeMeasure,
                        numerator: savedIndicator.numerator,
                        denominator: savedIndicator.denominator,
                    },
                    topicArea: {
                        externalReference: savedProblem.topicAreaExternalRef,
                        topicArea: savedProblem.topicAreaName,
                    },
                    grant: {externalReference: savedProblem.grantExternalRef, name: null},
                },
                facilityScreen: {
                    levelOfSupport: {label: savedProblem.levelOfSupport},
                    locationExternalReference: savedProblem.locationExternalRef,
                },
            }));
            navigateTo("problemOne");
        }
        setSpinnerText(null);
    }

    const handleLocationClick = async (locationExternalReference) => {
        setSpinnerText("Retrieving Problem");
        let savedVisit = null;
        let savedIndicator = null;
        let savedGrant = null;
        let savedStaffMember = null;
        let contactPersonList = null;
        let accompanyingPersonList = null;

        const savedProblem = await fetchProblem(context.problemToBeViewedExternalReference);

        if (savedProblem) {
            savedVisit = await fetchVisit(savedProblem.visitExternalRef);
            contactPersonList = savedVisit.metWith.map((contact) => {
                return {...contact, displayValue: createPersonDropdownDisplayValue(contact)}
            });

            accompanyingPersonList = savedVisit.companions.map((contact) => {
                return {...contact, displayValue: createPersonDropdownDisplayValue(contact, true, true)}
            });
        }

        const savedLocation = await fetchLocation(locationExternalReference);

        let levelOfSupportValue = null;
        switch (savedProblem.levelOfSupport) {
            case "National":
                levelOfSupportValue = 1;
                break;
            case "Provincial":
                levelOfSupportValue = 2;
                break;
            case "District":
                levelOfSupportValue = 3;
                break;
            case "Sub District":
                levelOfSupportValue = 4;
                break;
            case "Facility":
                levelOfSupportValue = 5;
                break;
        }

        if (savedVisit) {
            savedIndicator = await fetchIndicator(savedVisit.indicatorExternalReference);
        }

        if (savedVisit && savedIndicator) {
            savedStaffMember = await fetchStaffMember(savedVisit.systemUserExternalReference);
        }

        if (savedStaffMember) {
            savedGrant = await fetchGrant(savedProblem.grantExternalRef);
            let staffMember = {
                externalReference: savedStaffMember.externalReference,
                displayValue: createPersonDropdownDisplayValue(savedStaffMember, true, true)
            };

            let grant = {externalReference: null, name: "Select..."};
            if (savedGrant) {
                grant = {externalReference: savedGrant.externalReference, name: savedGrant.name}
            }

            setContext((prevState) => ({
                ...prevState,
                viewMode: true,
                breadcrumbs: [],
                changeIdeaScreen: null,
                copyOfTopicAreaScreen: null,
                copyOfProblemScreen: null,
                copyOfRootCauseScreen: null,
                copyOfChangeIdeaScreen: null,
                problemScreen: {
                    externalReference: savedProblem.externalReference,
                    title: savedProblem.name,
                    description: savedProblem.description,
                    nextVisitDate: savedVisit.dateOfNextVisit,
                    frequency: {
                        label: savedProblem.reviewFrequency,
                        value: savedProblem.reviewFrequency
                    },
                    baselines: savedProblem.outcomeMeasureBaselines,
                    toImprove: savedProblem.aimStatementVO.toImprove,
                    from: savedProblem.aimStatementVO.from,
                    to: savedProblem.aimStatementVO.to,
                    byDate: savedProblem.aimStatementVO.by,
                },
                rootCauseScreen: null,
                topicAreaScreen: {
                    dateOfVisit: savedVisit.dateOfVisit,
                    visitExternalReference: savedProblem.visitExternalRef,
                    indicator: savedIndicator,
                    topicArea: {
                        externalReference: savedProblem.topicAreaExternalRef,
                        topicArea: savedProblem.topicAreaName,
                    },
                    grant,
                    contactPersonList,
                    accompanyingPersonList,
                    staffMember,
                    fileList: savedVisit.fileVOList,
                },
                facilityScreen: {
                    levelOfSupport: {label: savedProblem.levelOfSupport, value: levelOfSupportValue},
                    country: {label: savedLocation.country, value: savedLocation.country},
                    province: {label: savedLocation.province, value: savedLocation.province},
                    district: {label: savedLocation.district, value: savedLocation.district},
                    subDistrict: {label: savedLocation.subDistrict, value: savedLocation.subDistrict},
                    facility: {label: savedLocation.facility, value: savedLocation.facility},
                    locationExternalReference: savedProblem.locationExternalRef,
                },
            }));
            navigateTo("facilitySearch");
        }
        setSpinnerText(null);
    }

    const handleVisitClick = async (externalReference) => {
        setSpinnerText("Retrieving Problem");
        let savedVisit = null;
        let savedIndicator = null;
        let savedGrant = null;
        let savedStaffMember = null;
        let contactPersonList = null;
        let accompanyingPersonList = null;
        const savedProblem = await fetchProblem(externalReference);

        if (savedProblem) {
            savedVisit = await fetchVisit(savedProblem.visitExternalRef);
            contactPersonList = savedVisit.metWith.map((contact) => {
                return {...contact, displayValue: createPersonDropdownDisplayValue(contact)}
            });

            accompanyingPersonList = savedVisit.companions.map((contact) => {
                return {...contact, displayValue: createPersonDropdownDisplayValue(contact, true, true)}
            });
        }

        if (savedVisit) {
            savedIndicator = await fetchIndicator(savedVisit.indicatorExternalReference);
        }

        if (savedVisit && savedIndicator) {
            savedStaffMember = await fetchStaffMember(savedVisit.systemUserExternalReference);
        }

        if (savedStaffMember) {
            savedGrant = await fetchGrant(savedProblem.grantExternalRef);
            let staffMember = {
                externalReference: savedStaffMember.externalReference,
                displayValue: createPersonDropdownDisplayValue(savedStaffMember, true, true)
            };

            let grant = {externalReference: null, name: "Select..."};
            if (savedGrant) {
                grant = {externalReference: savedGrant.externalReference, name: savedGrant.name}
            }

            setContext((prevState) => ({
                ...prevState,
                viewMode: true,
                breadcrumbs: [],
                changeIdeaScreen: null,
                copyOfTopicAreaScreen: null,
                copyOfProblemScreen: null,
                copyOfRootCauseScreen: null,
                copyOfChangeIdeaScreen: null,
                problemScreen: {
                    externalReference: savedProblem.externalReference,
                    title: savedProblem.name,
                    description: savedProblem.description,
                    nextVisitDate: savedVisit.dateOfNextVisit,
                    frequency: {
                        label: savedProblem.reviewFrequency,
                        value: savedProblem.reviewFrequency
                    },
                    baselines: savedProblem.outcomeMeasureBaselines,
                    toImprove: savedProblem.aimStatementVO.toImprove,
                    from: savedProblem.aimStatementVO.from,
                    to: savedProblem.aimStatementVO.to,
                    byDate: savedProblem.aimStatementVO.by,
                },
                rootCauseScreen: null,
                topicAreaScreen: {
                    dateOfVisit: savedVisit.dateOfVisit,
                    visitExternalReference: savedProblem.visitExternalRef,
                    indicator: savedIndicator,
                    topicArea: {
                        externalReference: savedProblem.topicAreaExternalRef,
                        topicArea: savedProblem.topicAreaName,
                    },
                    grant,
                    contactPersonList,
                    accompanyingPersonList,
                    staffMember,
                    fileList: savedVisit.fileVOList,
                },
                facilityScreen: {
                    levelOfSupport: {label: savedProblem.levelOfSupport},
                    locationExternalReference: savedProblem.locationExternalRef,
                },
            }));
            navigateTo("topicAreaOne");
        }
        setSpinnerText(null);
    }

    const buildLines = (words, lines) => {
        let line = "";
        let numberOfWordsToRemove = null;
        for (let i = 0; i < words.length; i++) {
            if (line.length < 20) {
                line = line + words[i] + " ";
            } else {
                lines.push(line);
                numberOfWordsToRemove = i;
                words.splice(0, numberOfWordsToRemove);
                buildLines(words, lines);
                break;
            }

            if (i === words.length - 1) {
                lines.push(line);
            }
        }
    }

    const customRenderFunction = (node) => {
        const nodeData = node.nodeDatum;

        const color = nodeData.attributes.color ? nodeData.attributes.color : "#5C9AD2";
        let {name} = nodeData;

        let words = name.split(" ");
        const lines = [];
        buildLines(words, lines);

        return (
            <g className="rd3t-node">
                <circle r="15" onClick={() => {
                    handleNodeClick(nodeData)
                }} style={{fill: color}}/>
                <g className="rd3t-label">
                    {lines.map((line, index) => {
                        return (
                            <text key={index} className="rd3t-label__title" x="30" y={20 * index}>{line}</text>
                        );
                    })}
                </g>
            </g>
        );
    }

    const handleDeleteModalShow = async (extRef) => {
        setShowModal(true);
        setExtRefToBeDeleted(extRef);
        const problem = await fetchProblem(extRef);
        setModalTitle(problem.name);
    }

    const handleDeleteProblemClick = async (externalReference) => {
        setSpinnerText("Deleting Problem");
        const [data, error] = await deleteProblem(context.username, externalReference);
        if (data) {
            showToaster(data);
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        setShowModal(false);
        navigateTo("problemSearchResults");
        setSpinnerText(null);
    }

    return (
        <div className="problem-tree-container">
            {problemTreeData && <Tree data={buildProblemTree()}
                                      collapsible={false}
                                      depthFactor={150}
                                      separation={{siblings: 2.5, nonSiblings: 2.5}}
                                      nodeSize={{x: 100, y: 100}}
                                      translate={{x: 400, y: 50}}
                                      zoom={0.8}
                                      orientation={"vertical"}
                                      renderCustomNodeElement={customRenderFunction}
            />}
            {context.activeSection === "My Problems" &&
            <div className={"problem-tree-button-container-back"}>
                <StandardButton text="Back" onClick={() => {
                    navigateTo("myProblems");
                }}/>
            </div>}
            {isAdmin && <div className={"problem-tree-button-container-delete"}>
                {context.activeSection === "Search Problems" &&
                <StandardButton text="Delete Problem"
                                onClick={() => {
                                    handleDeleteModalShow(context.problemToBeViewedExternalReference);
                                }}
                />}
            </div>}
            {isAdmin && <div className={"problem-tree-button-container-delete"}>
                {context.activeSection === "My Problems" &&
                <StandardButton text="Delete Problem"
                                onClick={() => {
                                    handleDeleteModalShow(context.problemToBeViewedExternalReference);
                                }}
                />}
            </div>}
            <div className={"problem-tree-button-container"}>
                {context.activeSection === "Search Problems" &&
                <StandardButton text="Search Results"
                                onClick={() => {
                                    navigateTo("problemSearchResults");
                                }}/>}
            </div>
            {showModal &&
            <ConfirmDeleteModal showModal={showModal}
                                setShowModal={setShowModal}
                                titleToDelete={modalTitle}
                                onDeleteClick={() => {
                                    handleDeleteProblemClick(extRefToBeDeleted);
                                }}
            />}
        </div>
    );
}
